// DEBT-VITA™ — Générateur Word (.docx) avec la lib `docx`
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from "docx";
import { saveAs } from "file-saver";

const GOLD = "C9A84C";
const BLUE = "1A3C5E";
const RED = "C0392B";
const BLACK = "0A0A0A";

function H1(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    children: [new TextRun({ text, bold: true, color: BLUE, size: 36 })],
    spacing: { before: 240, after: 120 },
  });
}
function H2(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    children: [new TextRun({ text, bold: true, color: BLUE, size: 28 })],
    spacing: { before: 200, after: 80 },
  });
}
function P(text, opts = {}) {
  return new Paragraph({
    children: [new TextRun({ text, ...opts })],
    spacing: { after: 80 },
  });
}
function fmt(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
  });
}
function fmtMoney(v, currency = "UM") {
  if (v === null || v === undefined) return "—";
  return `${fmt(v, 0)} ${currency}`;
}
function row(cells, headerStyle = false) {
  return new TableRow({
    children: cells.map((text) =>
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: String(text ?? ""),
                bold: headerStyle,
                color: headerStyle ? "FFFFFF" : "000000",
                size: 18,
              }),
            ],
          }),
        ],
        shading: headerStyle ? { fill: BLUE, color: "auto" } : undefined,
      })
    ),
  });
}

function tableFromRows(rowsAoa, headerCount = 1) {
  const allRows = rowsAoa.map((r, i) => row(r, i < headerCount));
  return new Table({
    rows: allRows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: "AAAAAA" },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: "AAAAAA" },
      left: { style: BorderStyle.SINGLE, size: 2, color: "DDDDDD" },
      right: { style: BorderStyle.SINGLE, size: 2, color: "DDDDDD" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "EEEEEE" },
      insideVertical: { style: BorderStyle.SINGLE, size: 2, color: "EEEEEE" },
    },
  });
}

export async function generateDebtWord({ project, params, results, narrative }) {
  const country = project?.country || "Pays non spécifié";
  const year = params?.fiscal_year || project?.fiscal_year || "";
  const currency = params?.currency || "UM";
  const horizonYear = new Date().getFullYear() + (params?.projection_years || 30);
  const alertLabel = results?.alert_level?.label || "INDETERMINE";

  const children = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: "VITAE-PUBLICA™", bold: true, color: GOLD, size: 24 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: "RAPPORT DEBT-VITA™", bold: true, color: BLACK, size: 56 }),
      ],
      spacing: { after: 240 },
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: "Analyse du Poids de l'Endettement Public en Vies Humaines",
          italics: true,
          size: 24,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: `${country} — Exercice ${year}`, bold: true, size: 28 }),
      ],
      spacing: { before: 200, after: 200 },
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: `NIVEAU D'ALERTE : ${alertLabel}`,
          bold: true,
          color: RED,
          size: 28,
        }),
      ],
      spacing: { after: 400 },
    }),

    H1("Avertissement méthodologique"),
    P(
      "Ce rapport traduit des données financières publiques en équivalents de vies humaines selon la Nouvelle Théorie des Finances Publiques d'Ahmed ELY Mustapha. Il constitue un outil de sensibilisation démocratique, non un document comptable officiel ni un avis juridique opposable."
    ),

    H1("Synthèse exécutive"),
    tableFromRows(
      [
        ["Indicateur", "Valeur"],
        ["Dette par contribuable", fmtMoney(results?.dette_par_tete_um, currency)],
        ["Jours de vie hypothéqués", fmt(results?.jours_vie_dette, 2) + " jours"],
        ["Années de vie hypothéquées", fmt(results?.annees_vie_dette, 4) + " ans"],
        ["% carrière sacrifié", fmt(results?.pct_carriere_dette, 3) + " %"],
        ["Service annuel en vies", fmt(results?.jours_vie_service_annuel, 2) + " j/an"],
        ["Charge enfant à l'horizon " + horizonYear, fmt(results?.annees_vie_enfant, 2) + " ans"],
        ["TAVD", fmt(results?.tavd, 2) + " %"],
        ["Niveau d'alerte vitaliste", alertLabel],
      ],
      1
    ),

    H1("Paramètres saisis"),
    tableFromRows(
      [
        ["Paramètre", "Valeur"],
        ["Dette publique totale", fmtMoney(params?.total_public_debt, currency)],
        ["Service annuel", fmtMoney(params?.annual_debt_service, currency)],
        ["Déficit fiscal annuel", fmtMoney(params?.annual_fiscal_deficit, currency)],
        ["PIB total", fmtMoney(params?.gdp_total, currency)],
        ["Ratio dette/PIB", fmt(params?.debt_to_gdp_ratio, 2) + " %"],
        ["Population totale", fmt(params?.total_population, 0)],
        ["Population active", fmt(params?.active_population, 0)],
        ["Source des données", params?.data_source || "—"],
      ],
      1
    ),
  ];

  // Créanciers
  const breakdown = results?.creditor_breakdown || [];
  if (breakdown.length > 0) {
    children.push(H1("Analyse par créancier"));
    children.push(
      tableFromRows(
        [
          ["Créancier", "Type", "Montant", "Part %", "Jours vie/tête"],
          ...breakdown.map((c) => [
            c.name || "—",
            c.type || "—",
            fmtMoney(c.amount, currency),
            fmt(c.part_pct, 2) + " %",
            fmt(c.jours_vie, 2) + " j",
          ]),
        ],
        1
      )
    );
  }

  // Projection
  const proj = results?.projection_data || [];
  if (proj.length > 0) {
    const decadal = proj.filter((_, i) => (i + 1) % 10 === 0);
    if (decadal.length === 0) decadal.push(proj[proj.length - 1]);
    children.push(H1("Projection intergénérationnelle"));
    children.push(
      tableFromRows(
        [
          ["Année", "Dette projetée", "Dette/tête", "Jours vie", "Années vie"],
          ...decadal.map((d) => [
            String(d.annee),
            fmtMoney(d.dette_totale, currency),
            fmtMoney(d.dette_par_tete, currency),
            fmt(d.jours_vie, 1) + " j",
            fmt(d.annees_vie, 3) + " ans",
          ]),
        ],
        1
      )
    );
  }

  // Narrative
  if (narrative) {
    children.push(H1("Analyse narrative vitaliste"));
    if (narrative.titre_analyse) {
      children.push(H2(narrative.titre_analyse));
    }
    if (narrative.synthese_vitaliste) {
      children.push(P(narrative.synthese_vitaliste));
    }
    const apg = narrative.analyse_par_generation || {};
    if (apg.generation_actuelle) {
      children.push(H2("Génération actuelle"));
      children.push(P(apg.generation_actuelle));
    }
    if (apg.generation_future) {
      children.push(H2("Génération future"));
      children.push(P(apg.generation_future));
    }
    if (apg.constat_intergen) {
      children.push(H2("Constat intergénérationnel"));
      children.push(P(apg.constat_intergen));
    }
    if (Array.isArray(narrative.points_critiques) && narrative.points_critiques.length) {
      children.push(H2("Points critiques"));
      narrative.points_critiques.forEach((p) =>
        children.push(P("• " + p))
      );
    }
    if (Array.isArray(narrative.recommandations_vitalistes) && narrative.recommandations_vitalistes.length) {
      children.push(H2("Recommandations"));
      narrative.recommandations_vitalistes.forEach((r, i) =>
        children.push(P(`${i + 1}. ${r}`))
      );
    }
    if (narrative.citation_ouvrage) {
      children.push(H2("Citation"));
      children.push(P('"' + narrative.citation_ouvrage + '"', { italics: true, color: GOLD }));
    }
    if (narrative.conclusion) {
      children.push(H2("Conclusion"));
      children.push(P(narrative.conclusion));
    }
  }

  // Footer
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: `\nVITAE-PUBLICA™ × DEBT-VITA™ — ${country} ${year} — ELY Mustapha (PMP · I-PMP · IBM Full Stack Developer)`,
          italics: true,
          size: 16,
          color: "777777",
        }),
      ],
      spacing: { before: 400 },
    })
  );

  const doc = new Document({
    creator: "VITAE-PUBLICA",
    title: `Rapport DEBT-VITA — ${country} ${year}`,
    styles: {
      default: {
        document: { run: { size: 22, font: "Calibri" } },
      },
    },
    sections: [{ children }],
  });

  const blob = await Packer.toBlob(doc);
  const today = new Date().toISOString().slice(0, 10);
  const countrySlug = (country && country !== "Pays non spécifié" ? country : "Pays_non_specifie").replace(/\s+/g, "_");
  const filename = `VITAE-PUBLICA_DEBT-VITA_${countrySlug}_${year || "----"}_${today}.docx`;
  saveAs(blob, filename);
  return filename;
}
